import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Notification } from '@/types/domain';
import { MOCK_NOTIFICATIONS } from '@/mocks/database';
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  X,
  Sparkles,
  ArrowRight,
} from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  title: string;
  message?: string;
  variant: ToastVariant;
  timestamp: string;
  durationMs?: number;
}

export interface CenterModalData {
  id: string;
  title: string;
  message: string;
  type: 'approval' | 'creation' | 'success' | 'warning' | 'error';
  entityId?: string;
  timestamp: string;
  autoCloseMs?: number;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (title: string, message: string, type?: Notification['type'], entityId?: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  markAsRead: (id: string) => void;
  toasts: ToastItem[];
  showToast: (title: string, message?: string, variant?: ToastVariant) => void;
  dismissToast: (id: string) => void;
  centerModal: CenterModalData | null;
  showCenterModal: (
    title: string,
    message: string,
    type?: CenterModalData['type'],
    entityId?: string,
    autoCloseMs?: number
  ) => void;
  closeCenterModal: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Event emitter listener helpers for cross-module notifications
type NotificationListener = (notification: Notification) => void;
type ToastListener = (toast: ToastItem) => void;
type CenterModalListener = (modal: CenterModalData | null) => void;

const notifListeners: Set<NotificationListener> = new Set();
const toastListeners: Set<ToastListener> = new Set();
const centerModalListeners: Set<CenterModalListener> = new Set();

// Global deduplication cache
let lastNotificationKey = '';
let lastNotificationTime = 0;
let lastModalKey = '';
let lastModalTime = 0;

/**
 * Determine toast variant based on notification type and title content
 */
function inferToastVariant(title: string, type: Notification['type']): ToastVariant {
  const lower = title.toLowerCase();
  if (
    lower.includes('approved') ||
    lower.includes('created') ||
    lower.includes('submitted') ||
    lower.includes('disbursed') ||
    lower.includes('completed') ||
    lower.includes('synced') ||
    lower.includes('reconciled') ||
    lower.includes('verified') ||
    lower.includes('success')
  ) {
    return 'success';
  }
  if (
    lower.includes('reject') ||
    lower.includes('cancel') ||
    lower.includes('fail') ||
    lower.includes('breach') ||
    lower.includes('duplicate') ||
    lower.includes('blocked') ||
    lower.includes('delete')
  ) {
    return 'error';
  }
  if (
    lower.includes('warning') ||
    lower.includes('delay') ||
    lower.includes('hold') ||
    lower.includes('spike') ||
    lower.includes('variance')
  ) {
    return 'warning';
  }
  if (type === 'ApprovalRequired') return 'warning';
  if (type === 'StatusChange') return 'success';
  return 'info';
}

/**
 * Global trigger to display a centered approval/creation confirmation modal
 */
export const showCenterModal = (
  title: string,
  message: string,
  type: CenterModalData['type'] = 'approval',
  entityId?: string,
  autoCloseMs: number = 3200
) => {
  const key = `${title}-${entityId || ''}`;
  const now = Date.now();
  if (key === lastModalKey && now - lastModalTime < 2800) {
    return; // Drop duplicate modal trigger
  }
  lastModalKey = key;
  lastModalTime = now;

  const newModal: CenterModalData = {
    id: `modal-${now}`,
    title,
    message,
    type,
    entityId,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    autoCloseMs,
  };

  centerModalListeners.forEach((listener) => listener(newModal));
};

export const closeCenterModal = () => {
  centerModalListeners.forEach((listener) => listener(null));
};

/**
 * Global trigger to send a notification, trigger modal if applicable, and render 1 centered toast
 */
export const notifyGlobal = (
  title: string,
  message: string,
  type: Notification['type'] = 'ApprovalRequired',
  entityId?: string
) => {
  const lower = title.toLowerCase();
  const isApproval =
    lower.includes('approved') ||
    lower.includes('approval') ||
    lower.includes('authorized') ||
    lower.includes('signoff');
  const isCreation =
    lower.includes('requisition submitted') ||
    lower.includes('requisition created') ||
    lower.includes('pr created');

  // Semantic category key for robust deduplication across disparate modules
  const categoryKey = isApproval
    ? `approval-${entityId || 'active'}`
    : isCreation
    ? `creation-${entityId || 'active'}`
    : `${title}-${entityId || ''}`;

  const now = Date.now();
  if (categoryKey === lastNotificationKey && now - lastNotificationTime < 3000) {
    return; // Drop rapid duplicate bursts across disparate services
  }
  lastNotificationKey = categoryKey;
  lastNotificationTime = now;

  const newNotif: Notification = {
    id: `notif-${now}-${Math.random().toString(36).substring(2, 6)}`,
    userId: 'CURRENT_USER',
    type,
    title,
    message,
    isRead: false,
    createdAt: new Date().toISOString(),
    entityId,
  };
  notifListeners.forEach((listener) => listener(newNotif));

  // If approval or creation event -> Show Center Modal popup!
  if (isApproval || isCreation) {
    showCenterModal(
      title,
      message,
      isApproval ? 'approval' : 'creation',
      entityId
    );
    // Modal is already prominently displayed in center, so suppress floating toast to avoid clutter
    return;
  }

  // Push to interactive floating toast queue (strictly 1 centered toast at a time)
  const variant = inferToastVariant(title, type);
  const newToast: ToastItem = {
    id: `toast-${now}-${Math.random().toString(36).substring(2, 6)}`,
    title,
    message,
    variant,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };
  toastListeners.forEach((listener) => listener(newToast));
};

/**
 * Global trigger to directly display a floating toast on screen (deduplicated)
 */
export const showToast = (
  title: string,
  message?: string,
  variant: ToastVariant = 'success'
) => {
  const key = `${title}-${message || ''}`;
  const now = Date.now();
  if (key === lastNotificationKey && now - lastNotificationTime < 2000) {
    return; // Drop duplicate toast
  }
  lastNotificationKey = key;
  lastNotificationTime = now;

  const newToast: ToastItem = {
    id: `toast-${now}-${Math.random().toString(36).substring(2, 6)}`,
    title,
    message,
    variant,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };
  toastListeners.forEach((listener) => listener(newToast));
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>(MOCK_NOTIFICATIONS);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [centerModal, setCenterModal] = useState<CenterModalData | null>(null);

  // Listen for background service notifications
  useEffect(() => {
    const handleNewNotif = (newNotif: Notification) => {
      setNotifications((prev) => [newNotif, ...prev]);
    };
    notifListeners.add(handleNewNotif);
    return () => {
      notifListeners.delete(handleNewNotif);
    };
  }, []);

  // Listen for floating toast dispatches (strictly 1 toast at a time, no duplicates)
  useEffect(() => {
    const handleNewToast = (toast: ToastItem) => {
      setToasts([toast]);
    };
    toastListeners.add(handleNewToast);
    return () => {
      toastListeners.delete(handleNewToast);
    };
  }, []);

  // Listen for centered confirmation modals
  useEffect(() => {
    const handleNewModal = (modal: CenterModalData | null) => {
      setCenterModal(modal);
    };
    centerModalListeners.add(handleNewModal);
    return () => {
      centerModalListeners.delete(handleNewModal);
    };
  }, []);

  // Auto-dismiss single toast after 3.5 seconds
  useEffect(() => {
    if (toasts.length === 0) return;

    const timer = setTimeout(() => {
      setToasts([]);
    }, 3500);

    return () => clearTimeout(timer);
  }, [toasts]);

  // Auto-dismiss center modal after duration (default 3.2s)
  useEffect(() => {
    if (!centerModal) return;

    const timer = setTimeout(() => {
      setCenterModal(null);
    }, centerModal.autoCloseMs || 3200);

    return () => clearTimeout(timer);
  }, [centerModal]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const addNotification = (
    title: string,
    message: string,
    type: Notification['type'] = 'ApprovalRequired',
    entityId?: string
  ) => {
    notifyGlobal(title, message, type, entityId);
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const markAsRead = (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAllAsRead,
        clearAll,
        markAsRead,
        toasts,
        showToast,
        dismissToast,
        centerModal,
        showCenterModal,
        closeCenterModal,
      }}
    >
      {children}

      {/* Centered Single Toast Container (Positioned top-middle) */}
      {typeof document !== 'undefined' &&
        toasts.length > 0 &&
        createPortal(
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[999999] flex flex-col items-center gap-2 max-w-md w-[92vw] sm:w-[420px] pointer-events-none font-poppins">
            {toasts.map((toast) => {
              const isSuccess = toast.variant === 'success';
              const isError = toast.variant === 'error';
              const isWarning = toast.variant === 'warning';

              return (
                <div
                  key={toast.id}
                  className={`pointer-events-auto w-full rounded-2xl p-3.5 border shadow-2xl backdrop-blur-xl transition-all duration-300 transform animate-in slide-in-from-top-4 fade-in zoom-in-95 ${
                    isSuccess
                      ? 'bg-card/95 border-emerald-500/40 text-foreground shadow-emerald-500/10'
                      : isError
                      ? 'bg-card/95 border-rose-500/40 text-foreground shadow-rose-500/10'
                      : isWarning
                      ? 'bg-card/95 border-amber-500/40 text-foreground shadow-amber-500/10'
                      : 'bg-card/95 border-primary/40 text-foreground shadow-primary/10'
                  }`}
                  role="alert"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`p-2 rounded-xl shrink-0 ${
                        isSuccess
                          ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                          : isError
                          ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400'
                          : isWarning
                          ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                          : 'bg-primary/15 text-primary'
                      }`}
                    >
                      {isSuccess ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : isError ? (
                        <AlertCircle className="h-4 w-4" />
                      ) : isWarning ? (
                        <AlertTriangle className="h-4 w-4" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-xs font-black text-foreground truncate font-poppins">
                          {toast.title}
                        </h4>
                        <span className="text-[10px] text-foreground-muted font-mono shrink-0">
                          {toast.timestamp}
                        </span>
                      </div>
                      {toast.message && (
                        <p className="text-[11px] text-foreground-secondary mt-0.5 leading-relaxed break-words font-poppins">
                          {toast.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => dismissToast(toast.id)}
                      className="text-foreground-muted hover:text-foreground p-1 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer shrink-0"
                      title="Dismiss"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Subtle countdown line */}
                  <div className="w-full bg-muted/40 h-0.5 mt-2.5 rounded-full overflow-hidden">
                    <div
                      className={`h-full animate-[shrink_3.5s_linear_forwards] ${
                        isSuccess
                          ? 'bg-emerald-500'
                          : isError
                          ? 'bg-rose-500'
                          : isWarning
                          ? 'bg-amber-500'
                          : 'bg-primary'
                      }`}
                    />
                  </div>
                </div>
              );
            })}
          </div>,
          document.body
        )}

      {/* Center Action Confirmation Modal (Enterprise Industry Standard Design) */}
      {typeof document !== 'undefined' &&
        centerModal &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000000] flex items-center justify-center bg-slate-950/60 backdrop-blur-xs p-4 animate-in fade-in duration-200"
            onClick={() => setCenterModal(null)}
          >
            <div
              className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl backdrop-blur-xl text-foreground font-poppins transform animate-in zoom-in-95 duration-200 select-none overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top Accent Gradient Bar */}
              <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500" />

              {/* Close Icon Button */}
              <button
                type="button"
                onClick={() => setCenterModal(null)}
                className="absolute top-4 right-4 p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted/80 transition-colors cursor-pointer"
                title="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>

              {/* Modal Body */}
              <div className="p-6 sm:p-7 flex flex-col items-center text-center">
                {/* Refined Enterprise Badge Icon */}
                <div className="h-12 w-12 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-3 shadow-sm">
                  <CheckCircle2 className="h-6 w-6 stroke-[2.2]" />
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[10.5px] font-bold tracking-wider uppercase bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 mb-2">
                  <Sparkles className="h-3 w-3" />
                  <span>{centerModal.type === 'approval' ? 'Approval Authorized' : 'Requisition Confirmed'}</span>
                </div>

                <h3 className="text-xl font-extrabold tracking-tight text-foreground">
                  {centerModal.title}
                </h3>

                <p className="text-xs sm:text-sm text-foreground-secondary mt-1.5 leading-relaxed max-w-md">
                  {centerModal.message}
                </p>

                {/* Structured Enterprise Summary Card */}
                <div className="w-full mt-5 rounded-xl border border-border/80 bg-muted/30 p-3.5 space-y-2.5 text-left text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-secondary font-medium">Document Reference:</span>
                    <span className="font-mono font-extrabold text-foreground bg-card px-2 py-0.5 rounded border border-border shadow-2xs">
                      {centerModal.entityId || 'REQ-RECORD'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-border/50 pt-2">
                    <span className="text-foreground-secondary font-medium">Enterprise Governance:</span>
                    <span className="inline-flex items-center gap-1.5 font-bold text-emerald-600 dark:text-emerald-400">
                      <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span>{centerModal.type === 'approval' ? 'Authorized & BAPI Validated' : 'Submitted & Logged'}</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-border/50 pt-2">
                    <span className="text-foreground-secondary font-medium">Downstream Routing:</span>
                    <span className="font-semibold text-foreground">
                      {centerModal.type === 'approval' ? 'PR Control Center & Steering NFA' : 'Department Approval Queue'}
                    </span>
                  </div>
                </div>

                {/* Modal Action Buttons */}
                <div className="w-full mt-6 flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setCenterModal(null)}
                    className="flex-1 py-2.5 px-4 rounded-xl border border-border text-foreground hover:bg-muted font-bold text-xs tracking-wide transition-colors cursor-pointer"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => setCenterModal(null)}
                    className="flex-1 py-2.5 px-4 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-xs tracking-wide shadow-sm transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>Continue</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Sleek Enterprise Countdown Progress Bar */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-muted/60 overflow-hidden">
                <div className="h-full bg-emerald-500 animate-[shrink_3.5s_linear_forwards]" />
              </div>
            </div>
          </div>,
          document.body
        )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
